import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { ModalService } from '../../services/modal.service';
import { NetWorthTrendModalComponent } from '../../../features/dashboard/widgets/net-worth-trend-widget/net-worth-trend-modal/net-worth-trend-modal';
import { ExpenseSelectionModalComponent } from '../modals/expense-selection-modal/expense-selection-modal';
import { InvestmentOverviewModalComponent } from '../../../features/dashboard/widgets/investment-overview-widget/investment-overview-modal/investment-overview-modal';
import { AiChatModalComponent } from '../../../features/dashboard/widgets/ai-chat-widget/ai-chat-modal/ai-chat-modal';
import { AiAdviceModalComponent } from '../../../features/dashboard/widgets/ai-advice-widget/ai-advice-modal/ai-advice-modal';
import { BudgetStatusModalComponent } from '../modals/budget-status-modal/budget-status-modal';
import { UpcomingBillsModalComponent } from '../modals/upcoming-bills-modal/upcoming-bills-modal';
import { ExpenseTrackerModalComponent } from '../modals/expense-tracker-modal/expense-tracker-modal';
import { SavingPlansModalComponent } from '../modals/saving-plans-modal/saving-plans-modal';

@Component({
  selector: 'app-footer',
  standalone: true,
  imports: [CommonModule, ExpenseSelectionModalComponent, NetWorthTrendModalComponent, InvestmentOverviewModalComponent, AiChatModalComponent, AiAdviceModalComponent, BudgetStatusModalComponent, UpcomingBillsModalComponent, ExpenseTrackerModalComponent, SavingPlansModalComponent],
  template: `
    <footer class="app-footer">
      <!-- Global Modals live here, outside the router-outlet transform context -->
      <app-expense-selection-modal 
        [isOpen]="isExpenseModalOpen"
        [accountsData]="expenseAccountsData"
        [creditCardsData]="expenseCreditCardsData"
        (selectionChange)="onExpenseAccountSelectionChange($event)"
        (close)="modalService.closeExpenseModal()">
      </app-expense-selection-modal>

      <app-net-worth-trend-modal
        [isOpen]="isNetWorthTrendModalOpen"
        (close)="modalService.closeNetWorthTrendModal()">
      </app-net-worth-trend-modal>

      <app-investment-overview-modal
        [isOpen]="isInvestmentOverviewModalOpen"
        [assetName]="selectedAssetName"
        (close)="modalService.closeInvestmentOverviewModal()">
      </app-investment-overview-modal>

      <app-ai-chat-modal
        [isOpen]="isAiChatModalOpen"
        (close)="modalService.closeAiChatModal()">
      </app-ai-chat-modal>

      <app-ai-advice-modal
        [isOpen]="isAiAdviceModalOpen"
        (close)="modalService.closeAiAdviceModal()"
        (tellMeMore)="onTellMeMore()">
      </app-ai-advice-modal>

      <app-budget-status-modal
        [isOpen]="isBudgetStatusModalOpen"
        (close)="modalService.closeBudgetStatusModal()">
      </app-budget-status-modal>

      <app-upcoming-bills-modal
        [isOpen]="isUpcomingBillsModalOpen"
        (close)="modalService.closeUpcomingBillsModal()">
      </app-upcoming-bills-modal>

      <app-expense-tracker-modal
        [isOpen]="isExpenseTrackerModalOpen"
        (close)="modalService.closeExpenseTrackerModal()">
      </app-expense-tracker-modal>

      <app-expense-selection-modal 
        [isOpen]="isAccountSummaryModalOpen"
        [accountsData]="accountSummaryAccountsData"
        [creditCardsData]="accountSummaryCreditCardsData"
        title="Account Summary"
        subtitle="Select accounts to track statistics."
        (close)="modalService.closeAccountSummaryModal()">
      </app-expense-selection-modal>

      <app-saving-plans-modal
        [isOpen]="isSavingPlansModalOpen"
        (close)="modalService.closeSavingPlansModal()">
      </app-saving-plans-modal>
    </footer>
  `,
  styles: [`
    .app-footer {
      position: relative;
      z-index: 10000;
    }
  `]
})
export class FooterComponent implements OnInit, OnDestroy {
  isExpenseModalOpen = false;
  isNetWorthTrendModalOpen = false;
  isInvestmentOverviewModalOpen = false;
  isAiChatModalOpen = false;
  isAiAdviceModalOpen = false;
  isBudgetStatusModalOpen = false;
  isUpcomingBillsModalOpen = false;
  isExpenseTrackerModalOpen = false;
  isAccountSummaryModalOpen = false;
  accountSummaryAccountsData: any[] = [];
  accountSummaryCreditCardsData: any[] = [];
  isSavingPlansModalOpen = false;
  selectedAssetName = '';
  expenseAccountsData: any[] = [];
  expenseCreditCardsData: any[] = [];
  private subs = new Subscription();

  constructor(
    public modalService: ModalService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.subs.add(
      this.modalService.expenseModalOpen$.subscribe(state => {
        this.isExpenseModalOpen = state.open;
        this.expenseAccountsData = state.accountsData;
        this.expenseCreditCardsData = state.creditCardsData;
        this.cdr.detectChanges();
      })
    );

    this.subs.add(
      this.modalService.netWorthTrendModalOpen$.subscribe(open => {
        this.isNetWorthTrendModalOpen = open;
        this.cdr.detectChanges();
      })
    );

    this.subs.add(
      this.modalService.investmentOverviewModalOpen$.subscribe(state => {
        this.isInvestmentOverviewModalOpen = state.open;
        this.selectedAssetName = state.assetName;
        this.cdr.detectChanges();
      })
    );

    this.subs.add(
      this.modalService.aiChatModalOpen$.subscribe(open => {
        this.isAiChatModalOpen = open;
        this.cdr.detectChanges();
      })
    );

    this.subs.add(
      this.modalService.aiAdviceModalOpen$.subscribe(open => {
        this.isAiAdviceModalOpen = open;
        this.cdr.detectChanges();
      })
    );

    this.subs.add(
      this.modalService.budgetStatusModalOpen$.subscribe(open => {
        this.isBudgetStatusModalOpen = open;
        this.cdr.detectChanges();
      })
    );

    this.subs.add(
      this.modalService.upcomingBillsModalOpen$.subscribe(open => {
        this.isUpcomingBillsModalOpen = open;
        this.cdr.detectChanges();
      })
    );

    this.subs.add(
      this.modalService.expenseTrackerModalOpen$.subscribe(open => {
        this.isExpenseTrackerModalOpen = open;
        this.cdr.detectChanges();
      })
    );

    this.subs.add(
      this.modalService.accountSummaryModalOpen$.subscribe(state => {
        this.isAccountSummaryModalOpen = state.open;
        this.accountSummaryAccountsData = state.accountsData;
        this.accountSummaryCreditCardsData = state.creditCardsData;
        this.cdr.detectChanges();
      })
    );

    this.subs.add(
      this.modalService.savingPlansModalOpen$.subscribe(open => {
        this.isSavingPlansModalOpen = open;
        this.cdr.detectChanges();
      })
    );
  }

  onTellMeMore() {
    this.modalService.closeAiAdviceModal();
    this.modalService.openAiChatModal();
  }

  onExpenseAccountSelectionChange(selectedAccounts: any[]) {
    this.modalService.updateExpenseAccountSelection(selectedAccounts);
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }
}
